// Document contents tab: table-of-contents tree, the selected clause detail and the toggleable source preview (S3 PdfViewer slot focused on the clause), each pane scrolling on its own.
import { useQuery } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { clausesGetClauseOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { Pane, SplitPane } from "@/components/common/SplitPane";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ClauseDetail, NoClauseSelected } from "@/features/library/ClauseDetail";
import { ClauseSearch } from "@/features/library/ClauseSearch";
import { ClauseTree } from "@/features/library/ClauseTree";
import { PdfViewer } from "@/features/source/PdfViewer";
import { cn } from "@/lib/cn";

interface ContentsTabProps {
  docUid: string;
  region: string;
  clauseUid: string | null;
  preview: boolean;
  onSelect: (clauseUid: string) => void;
  onPreviewChange: (preview: boolean) => void;
}

export function nonceOf(text: string): number {
  let hash = 0;
  for (const char of text) {
    hash = (Math.imul(hash, 31) + (char.codePointAt(0) ?? 0)) | 0;
  }
  return hash;
}

function SourcePreview({
  docUid,
  clauseUid,
  onClose,
}: {
  docUid: string;
  clauseUid: string;
  onClose: () => void;
}) {
  const detail = useQuery(clausesGetClauseOptions({ path: { clause_uid: clauseUid } }));
  const start = detail.data?.clause.page_start ?? 1;
  const [pages, setPages] = useState<Record<string, number>>({});
  const page = pages[clauseUid] ?? start;
  const scrollTo = useMemo(
    () => ({ target: "clause" as const, nonce: nonceOf(`${clauseUid}@${String(start)}`) }),
    [clauseUid, start],
  );
  return (
    <Pane as="aside" label="Source preview" border="left" scroll={false} surface="viewer">
      <PdfViewer
        docUid={docUid}
        page={page}
        onPageChange={(next) => {
          setPages((current) => ({ ...current, [clauseUid]: next }));
        }}
        focus={{ kind: "clause", clauseUid }}
        scrollTo={scrollTo}
        highlightsVisible
        toolbarStart={
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-xs font-semibold text-ink-2">Source</span>
            <Button variant="ghost" size="icon-sm" aria-label="Close the source preview" onClick={onClose}>
              <XIcon aria-hidden />
            </Button>
          </div>
        }
        className="min-h-0 flex-1"
      />
    </Pane>
  );
}

export function ContentsTab({
  docUid,
  region,
  clauseUid,
  preview,
  onSelect,
  onPreviewChange,
}: ContentsTabProps) {
  const detail = useQuery({
    ...clausesGetClauseOptions({ path: { clause_uid: clauseUid ?? "" } }),
    enabled: clauseUid !== null,
  });
  const revealUids = useMemo(
    () => (detail.data ? detail.data.path.map((ancestor) => ancestor.clause_uid) : []),
    [detail.data],
  );
  const showPreview = preview && clauseUid !== null;
  return (
    <Card className="h-[max(560px,calc(100dvh-var(--topbar-height)-230px))] overflow-hidden">
      <SplitPane
        className={cn(
          "grid-cols-[minmax(260px,340px)_minmax(0,1fr)]",
          showPreview && "xl:grid-cols-[300px_minmax(0,1fr)_380px] 2xl:grid-cols-[340px_minmax(0,1fr)_420px]",
        )}
      >
        <Pane as="nav" label="Table of contents" border="right" scroll={false} className="bg-subtle">
          <ClauseSearch docUid={docUid} selectedUid={clauseUid} onSelect={onSelect}>
            <ClauseTree
              docUid={docUid}
              selectedUid={clauseUid}
              revealUids={revealUids}
              onSelect={onSelect}
              className="min-h-0 flex-1"
            />
          </ClauseSearch>
        </Pane>
        <Pane label="Clause">
          {clauseUid === null ? (
            <NoClauseSelected />
          ) : (
            <ClauseDetail
              key={clauseUid}
              docUid={docUid}
              region={region}
              clauseUid={clauseUid}
              previewOpen={showPreview}
              onTogglePreview={() => {
                onPreviewChange(!showPreview);
              }}
              onSelect={onSelect}
            />
          )}
        </Pane>
        {showPreview ? (
          <SourcePreview
            docUid={docUid}
            clauseUid={clauseUid}
            onClose={() => {
              onPreviewChange(false);
            }}
          />
        ) : null}
      </SplitPane>
    </Card>
  );
}
